import { Button, Stack, Typography } from "@mui/material"
import { ImagePreviews } from "../../../shared/components/ImagePreviews"
import { AddImages } from "../../../components/AddImages"
import { ImagePreview } from "../EstimateGateDetailComponent"
import { getCustomToken } from "../../../shared/services/auth.service"
import { isAndroidDevice } from "../../../shared/misc/dom-utils"
import { isIosDevice } from "../../../shared/misc/dom-utils"
import { useNavigate } from "react-router-dom"
import { useUserSettings } from "../../../components/UserSettingsContext"
import { EstimateTreeDto, UpdateEstimateTreeDto, WorkItemType } from "../../../shared/dtos/estimate-tree.dto"

interface EstimateTreeDetailImagesProps {
  savedTree: EstimateTreeDto,
  setUpdatedTree: React.Dispatch<React.SetStateAction<UpdateEstimateTreeDto>>,
  treeImagePreviews: ImagePreview[],
  setTreeImagePreviews: React.Dispatch<React.SetStateAction<ImagePreview[]>>,
  openImageDialog: (imagePreview: ImagePreview) => void,
  imageDialogOpen?: boolean,
  onViewerOpenChange?: (open: boolean) => void,
  countTreeImagesLoading: number,
  handleCompressedTreeImage: (files: File[]) => void,
  canEdit: boolean,
  /** False in library preparation: staff correct records at a desk, not measure in the field. */
  measuredPictureEnabled?: boolean,
}

export const EstimateTreeDetailImagesControls = ({savedTree, setUpdatedTree, treeImagePreviews, setTreeImagePreviews, openImageDialog, imageDialogOpen, onViewerOpenChange, countTreeImagesLoading, handleCompressedTreeImage, canEdit, measuredPictureEnabled = true}: EstimateTreeDetailImagesProps) => {
  const navigate = useNavigate();
  const {settings} = useUserSettings();

  const removeTreeImage = (index: number) => {
    const removedImage = treeImagePreviews[index];
    setTreeImagePreviews(prevImages => prevImages.filter((_, i) => i !== index));
    setUpdatedTree(prevTree => {
      if(savedTree.images.find(image => image.id === removedImage.id)) {
        return {
          ...prevTree!,
          imageIdsToRemove: [...(prevTree!.imageIdsToRemove ?? []), removedImage.id],
        }
      }
      if(prevTree.imageIdsToAdd?.includes(removedImage.id)) {
        return {
          ...prevTree!,
          imageIdsToAdd: prevTree.imageIdsToAdd.filter(id => id !== removedImage.id),
        }
      }
      return prevTree;
    });
  };

  const workItemTypeSupportsMeasuredPicture = (workItemType: WorkItemType) => {
    switch (workItemType) {
      case WorkItemType.Tree:
      case WorkItemType.TreeGroup:
        return true;
      default:
        return false;
    }
  }
    

  const handleTakeMeasuredPictureClick = async () => {
    const isAndroid = isAndroidDevice();
    const isIos = isIosDevice();
    if (!isAndroid && !isIos) {
      alert('This feature is only available on android or ios devices. (Current device: ' + navigator.userAgent + ')');
      return;
    }
    // Hand the Android app a Firebase custom token (not our ID token); it signs in with
    // it (signInWithCustomToken) to get its own real, self-refreshing Firebase session.
    // The iPhone app keeps its own session, so it gets none.
    const customToken = isAndroid ? await getCustomToken(navigate) : undefined;
    const baseDeepLink = isAndroid
      ? process.env.REACT_APP_ANDROID_APP_BASE_DEEP_LINK
      : process.env.REACT_APP_IOS_APP_BASE_DEEP_LINK;
    const deepLink = `${baseDeepLink}/measure-tree?${customToken ? `token=${customToken}&` : ''}estimate-id=${savedTree.estimateId}&tree-id=${savedTree.id}`;
    console.log(`Opening ${isAndroid ? 'Android' : 'iOS'} app with deep link:`, deepLink);
    window.location.replace(deepLink);
  }

  const WorkItemSpecificInstructions = () => {
    switch (savedTree.workItemType) {
      case "TreeGroup":
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Find the average tree size within the group and measure that tree
          </Typography>
        )
      default:
        return (<></>)
    }
  }

  return (
    <Stack spacing={2} data-review-field="photos">
      <ImagePreviews images={treeImagePreviews} removeImage={removeTreeImage} openImageDialog={openImageDialog} imageDialogOpen={imageDialogOpen} onViewerOpenChange={onViewerOpenChange} countTreeImagesLoading={countTreeImagesLoading} canRemoveImage={canEdit} />
      {canEdit && <AddImages onImagesAdded={handleCompressedTreeImage} imageQuality={settings.imageUploadQuality}/>}
      {canEdit && measuredPictureEnabled && workItemTypeSupportsMeasuredPicture(savedTree.workItemType) && <Button onClick={handleTakeMeasuredPictureClick} variant="contained" component="label">
        Take Measured Picture
      </Button>}
      <WorkItemSpecificInstructions />
    </Stack>
  )
}